import { Injectable, inject } from '@angular/core';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { ApplePay } from '@df/module-apple-pay/apple-pay.namespace';
import { IBasketTotals } from '@df/session/api/basket/basket';
import { OpayoApplePay } from './opayo-apple-pay';

const SHIPPING_METHOD_SEPARATOR = 'shippingseparator';

@Injectable({ providedIn: 'root' })
export class OpayoApplePayService {
    private readonly app = inject(App);

    /**
     * Get merchant session key from Opayo
     * Required before initiating Apple Pay session
     * Session key expires after configured time (default: 400 seconds)
     */
    getMerchantSessionKey(status?: Status): Promise<OpayoApplePay.IMerchantSessionKeyResponse> {
        return this.app.request.send<OpayoApplePay.IMerchantSessionKeyResponse>(status, {
            path: OpayoApplePay.ENDPOINTS.MERCHANT_SESSION_KEY,
            data: {}
        });
    }

    /**
     * Request Apple Pay session from Opayo
     * Requires valid domain name registered with Apple
     */
    getApplePaySession(domainName: string, status?: Status): Promise<OpayoApplePay.IApplePaySessionResponse> {
        if (!domainName) {
            return Promise.reject(new Error('Domain name is required'));
        }

        const requestData: OpayoApplePay.IApplePaySessionRequest = {
            domainName
        };

        return this.app.request.send<OpayoApplePay.IApplePaySessionResponse>(status, {
            path: OpayoApplePay.ENDPOINTS.SESSION,
            data: requestData
        });
    }

    /**
     * Process Apple Pay payment through Opayo
     */
    processTransaction(request: OpayoApplePay.ITransactionRequest, status?: Status): Promise<OpayoApplePay.ITransactionResponse> {
        if (!request.merchantSessionKey) {
            return Promise.reject(new Error('Merchant session key is required'));
        }

        return this.app.request.send<OpayoApplePay.ITransactionResponse>(status, {
            path: OpayoApplePay.ENDPOINTS.TRANSACTION,
            data: request
        });
    }

    /**
     * Helper method to get current quote ID from basket
     */
    getQuoteId(): number | undefined {
        const basket = this.app.customer?.basket;
        return basket?.data?.quote_id;
    }

    /**
     * Helper method to get basket grand total
     */
    getAmount(): number | undefined {
        const totals = this.app.customer?.basket?.totals;
        return totals?.grand_total;
    }

    /**
     * Helper method to get currency code
     */
    getCurrency(): string {
        const currency = this.app.multisite?.current?.currency;
        return currency || OpayoApplePay.PAYMENT_CONFIG.DEFAULT_CURRENCY;
    }

    /**
     * Check if basket has items
     */
    hasBasketItems(): boolean {
        const items = this.app.customer?.basket?.items;
        return Array.isArray(items) && items.length > 0;
    }

    // ========== Payment Request Builder Methods ==========

    /**
     * Build complete payment request
     */
    buildPaymentRequest(
        totals: IBasketTotals | undefined,
        merchantName: string,
        isVirtual: boolean,
        isExpress: boolean,
        shippingMethods?: OpayoApplePay.IShippingMethodExtended[]
    ): OpayoApplePay.IApplePayPaymentRequest | undefined {
        const totalLine = this.buildTotalLine(totals, merchantName);

        if (!totalLine) {
            return undefined;
        }

        const data = {
            countryCode: OpayoApplePay.PAYMENT_CONFIG.COUNTRY_CODE,
            currencyCode: OpayoApplePay.PAYMENT_CONFIG.CURRENCY_CODE,
            total: totalLine,
            supportedNetworks: ['masterCard', 'visa', 'discover', 'maestro'],
            merchantCapabilities: [ApplePay.MerchantCapabilityType.supports3DS]
        } as OpayoApplePay.IApplePayPaymentRequest;
        if (isExpress) {
            data.lineItems = this.buildLineItems(totals);
            data.shippingMethods = this.buildShippingMethods(shippingMethods);
            data.requiredShippingContactFields = this.buildRequiredShippingFields(isVirtual);
            data.requiredBillingContactFields = this.buildRequiredBillingFields();
        }
        return data;
    }

    /**
     * Build total line item
     */
    buildTotalLine(
        totals: IBasketTotals | undefined,
        merchantName: string,
        type: ApplePay.LineItemType = ApplePay.LineItemType.final
    ): ApplePay.ILineItem | undefined {
        const total = totals?.grand_total;

        if (!total) {
            return undefined;
        }

        return {
            amount: total.toString(),
            label: merchantName || 'Apple Pay',
            type
        };
    }

    /**
     * Build line items from basket totals
     */
    buildLineItems(totals: IBasketTotals | undefined): ApplePay.ILineItem[] {
        const items: ApplePay.ILineItem[] = [];

        if (!totals) {
            return items;
        }

        // Add shipping
        if (totals.shipping) {
            items.push({
                label: 'Shipping',
                amount: totals.shipping.toString(),
                type: ApplePay.LineItemType.final
            });
        }

        // Add discount
        if (totals.discount && totals.discount > 0) {
            items.push({
                label: 'Discount',
                amount: (-totals.discount).toString(),
                type: ApplePay.LineItemType.final
            });
        }

        return items;
    }

    /**
     * Build shipping methods
     */
    buildShippingMethods(shippingMethods?: OpayoApplePay.IShippingMethodExtended[]): ApplePay.IShippingMethod[] {
        const methods: ApplePay.IShippingMethod[] = [];

        if (shippingMethods && Array.isArray(shippingMethods)) {
            shippingMethods.forEach(method => {
                methods.push(this.mapShippingMethod(method));
            });
        }

        if (methods.length === 0) {
            methods.push(this.getDefaultShippingMethod());
        }

        return methods;
    }

    /**
     * Map shipping method to Apple Pay format
     */
    mapShippingMethod(method: OpayoApplePay.IShippingMethodExtended): ApplePay.IShippingMethod {
        return {
            label: method.title,
            detail: method.lead_time || '',
            amount: (method.price || 0).toString(),
            identifier: this.getShippingMethodIdentifier(method.code)
        };
    }

    /**
     * Creates apple pay shipping method identifier
     * @param {string} code
     * @returns {string}
     */
    protected getShippingMethodIdentifier(code: string): string {
        return code + SHIPPING_METHOD_SEPARATOR + Date.now().toString();
    }

    /**
     * Extract shipping method code from apple pay shipping method identifier
     * @param {string} identifier
     * @returns {string | undefined}
     */
    getShippingMethodCodeFromIdentifier(identifier: string): string | undefined {
        const parts = identifier.split(SHIPPING_METHOD_SEPARATOR);
        if (parts?.length) {
            return parts[0];
        }
        return undefined;
    }

    /**
     * Get default shipping method
     */
    private getDefaultShippingMethod(): ApplePay.IShippingMethod {
        return {
            label: OpayoApplePay.DEFAULT_SHIPPING.LABEL,
            detail: OpayoApplePay.DEFAULT_SHIPPING.DETAIL,
            amount: OpayoApplePay.DEFAULT_SHIPPING.AMOUNT,
            identifier: OpayoApplePay.DEFAULT_SHIPPING.IDENTIFIER
        };
    }

    /**
     * Build required shipping fields
     */
    private buildRequiredShippingFields(isVirtual: boolean): ApplePay.ContactField[] {
        if (isVirtual) {
            return [];
        }

        return [ApplePay.ContactField.postalAddress, ApplePay.ContactField.email, ApplePay.ContactField.phone];
    }

    /**
     * Build required shipping fields
     */
    private buildRequiredBillingFields(): ApplePay.ContactField[] {
        return [ApplePay.ContactField.postalAddress];
    }

    // ========== Validator Methods ==========

    /**
     * Validate session response
     */
    isValidSessionResponse(response: OpayoApplePay.IApplePaySessionResponse): boolean {
        return response.success === true && response.session !== undefined;
    }

    /**
     * Validate transaction response
     */
    isValidTransactionResponse(response: OpayoApplePay.ITransactionResponse): boolean {
        return response.success === true && response.approved === true;
    }

    /**
     * Check if Apple Pay is available
     */
    isApplePayAvailable(): boolean {
        return typeof window !== 'undefined' && window.ApplePaySession !== undefined;
    }

    /**
     * Validate merchant session key
     */
    isValidMerchantSessionKey(key: string | undefined): boolean {
        return typeof key === 'string' && key.length > 0;
    }

    /**
     * Extract domain from URL
     */
    extractDomainName(validationURL: string): string {
        return window.location.hostname;

        // try {
        //     const url = new URL(validationURL);
        //     return url.hostname || window.location.hostname;
        // } catch {
        //     return window.location.hostname;
        // }
    }

    // ========== Parser Methods ==========

    /**
     * Parse payment token
     */
    parsePaymentToken(token: unknown): unknown {
        if (typeof token === 'string') {
            try {
                return JSON.parse(token);
            } catch {
                return token;
            }
        }
        return token;
    }

    /**
     * Extract item label from basket item
     */
    extractItemLabel(item: unknown): string {
        const typedItem = item as {
            name?: string;
            product?: {
                name?: string;
                data?: { name?: string };
            };
        };

        return typedItem.name || typedItem.product?.name || typedItem.product?.data?.name || 'Item';
    }

    /**
     * Extract item amount from basket item
     */
    extractItemAmount(item: unknown): number {
        const typedItem = item as { row_total?: number };
        return typedItem.row_total || 0;
    }
}
